import {
  defaultForgeCopy,
  FORGE_BOX_SORTS,
  type ForgeBoxSort,
  type ForgeBoxSummary,
  type ForgeCopy,
  VOLATILITY_WORDS,
} from "@loot-os/api-contract";
import type {
  ForgeCardContent,
  ForgeContent,
  ForgeGridState,
  ForgeHeadingContent,
} from "@loot-os/design-system/templates/forge/forgeContent";
import type { TemplateAction } from "@loot-os/design-system/templates/shared/content";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useCurrency } from "../../../lib/currency.js";
import { forgeErrorMessage } from "../../../lib/forge.js";
import { forgeBoxesInfiniteQueryOptions } from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTerminology } from "../../../lib/terminology.js";
import { topCardOf } from "../shared/boxCards.js";

/** A shopper-built box as its card, linking to its page: the Forge's grid and Home's showcase. */
export function forgeCardOf(forged: ForgeBoxSummary, copy: ForgeCopy["grid"]): ForgeCardContent {
  return {
    box: forged,
    href: `/boxes/${forged.id}`,
    byline: copy.byline(forged.forge.creatorDisplayName),
    topCard: topCardOf(forged),
    volatility: forged.volatility,
    volatilityWords: VOLATILITY_WORDS,
  };
}

/** The forge's top, and what a shopper can do from it: their own boxes and create, or sign in. */
function useForgeHeadingContent(copy: ForgeCopy["heading"]): ForgeHeadingContent {
  const { user } = useShopper();
  const actions: TemplateAction[] = user
    ? [
        { label: copy.mine, href: "/forge/mine", emphasis: "secondary" },
        { label: copy.create, href: "/forge/create", emphasis: "primary" },
      ]
    : [{ label: copy.signIn, href: "/login", emphasis: "secondary" }];
  return {
    trail: [{ label: copy.home, href: "/" }],
    current: copy.title,
    title: copy.title,
    intro: copy.intro,
    actions,
  };
}

/** GET /v1/forge/boxes: the boxes shoppers built, newest or most opened first, a page at a time. */
export function useForgeContent(): ForgeContent {
  const terms = useTerminology();
  const copy = defaultForgeCopy(terms);
  const currency = useCurrency();
  const [sort, setSort] = useState<ForgeBoxSort>("new");
  const query = useInfiniteQuery(forgeBoxesInfiniteQueryOptions(sort));
  const boxes = query.data?.pages.flatMap((page) => page.data) ?? [];

  let state: ForgeGridState;
  if (query.isPending) state = { status: "loading" };
  else if (query.isError && boxes.length === 0) {
    state = {
      status: "error",
      message: forgeErrorMessage(query.error, { terms, currency }),
    };
  } else if (boxes.length === 0) {
    state = {
      status: "empty",
      message: copy.grid.empty,
      action: null,
    };
  } else {
    state = {
      status: "ready",
      cards: boxes.map((forged) => forgeCardOf(forged, copy.grid)),
    };
  }

  return {
    heading: useForgeHeadingContent(copy.heading),
    sort: {
      value: sort,
      label: copy.sort.label,
      options: FORGE_BOX_SORTS.map((value) => ({ value, label: copy.sort.options[value] })),
      onValueChange: setSort,
    },
    grid: {
      title: copy.grid.title,
      currency,
      state,
      more:
        state.status === "ready" && query.hasNextPage
          ? {
              shown: boxes.length,
              loading: query.isFetchingNextPage,
              label: copy.grid.loadMore,
              onLoadMore: () => void query.fetchNextPage(),
            }
          : null,
    },
  };
}
